import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-heart-3-line",
    title: "专注爱情题材",
    desc: "我们只做一件事——把爱情电影讲深、讲透，让每一次观影都有迹可循。",
  },
  {
    icon: "ri-quill-pen-line",
    title: "真实的深度影评",
    desc: "拒绝人云亦云。每一篇影评都来自观影后的真实感受与细节考据。",
  },
  {
    icon: "ri-compass-3-line",
    title: "贴心的观影指南",
    desc: "从剧情解析到幕后花絮，帮助你更完整地理解一部作品想说的话。",
  },
];

const timeline = [
  { year: "2019", text: "「爱的色放电影」上线，从一份私人片单开始。" },
  { year: "2021", text: "建立影评编辑部，开始系统整理爱情电影资料。" },
  { year: "2023", text: "推出影评专题策划，聚合主题看片指南。" },
  { year: "至今", text: "持续更新，与三十万影迷一起读懂电影里的爱。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 爱的色放电影",
    description:
      "了解爱的色放电影的品牌定位、服务内容与创作理念。我们专注于爱情电影的介绍、剧情解析与影评聚合，陪你读懂每一段光影里的深情。",
    keywords: "关于我们,爱的色放电影,爱情电影平台,影评网站,观影指南",
  });

  return (
    <div className="pb-12">
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20illustration%20of%20a%20cozy%20vintage%20cinema%20hall%20with%20warm%20glowing%20lights%20and%20empty%20rose%20colored%20seats%2C%20soft%20rose%20and%20ivory%20color%20palette%20with%20golden%20accents%2C%20cinematic%20wide%20composition%2C%20dreamy%20nostalgic%20atmospheric%20mood%2C%20painterly%20textured%20background%2C%20no%20text%2C%20high%20detail&width=1600&height=900&seq=about-hero-01&orientation=landscape"
          alt="爱的色放电影 品牌形象"
          title="爱的色放电影 - 关于我们"
          className="w-full h-full object-top object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/40 to-foreground-950/20"></div>
        <div className="relative z-10 h-full w-full px-4 md:px-10 flex flex-col justify-end pb-8 md:pb-12">
          <div className="max-w-[1440px] mx-auto w-full">
            <h1 className="font-heading font-bold text-background-50 text-2xl md:text-4xl">
              关于爱的色放电影
            </h1>
            <p className="mt-3 text-background-200 text-sm md:text-lg max-w-2xl">
              一个专注爱情题材的影视资料与影评平台，因热爱而生
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-10 pt-12">
        <div className="max-w-[1100px] mx-auto">
          {/* positioning */}
          <section className="mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1 h-6 rounded-full bg-primary-500"></span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                网站定位
              </h2>
            </div>
            <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-loose">
              <p>
                爱的色放电影是一个以爱情题材为核心的影视资讯平台。我们相信，一部好的爱情电影不只是消遣，它更像一面镜子，照见我们曾经的心动、犹豫与告别。
              </p>
              <p>
                因此，我们不满足于提供一份简单的片单。我们为每一部收录的电影整理详尽的资料——包括剧情简介、分段的剧情解析、幕后花絮以及多维度的专业影评，帮助你在观影前后都能找到值得停留的细节。
              </p>
              <p>
                无论你是想重温经典，还是想寻找下一部要看的爱情片，我们都希望这里能成为你观影路上的一盏灯，陪你把每一段光影里的深情，读得更完整。
              </p>
            </div>
          </section>

          {/* values */}
          <section className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-6 rounded-full bg-primary-500"></span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                我们坚持的事
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-lg bg-background-50 border border-background-200 p-6"
                >
                  <span className="w-12 h-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-2xl`}></i>
                  </span>
                  <h3 className="font-heading font-semibold text-base text-foreground-950">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* timeline */}
          <section className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-6 rounded-full bg-primary-500"></span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                一路走来
              </h2>
            </div>
            <div className="flex flex-col gap-5">
              {timeline.map((t) => (
                <div key={t.year} className="flex gap-5">
                  <span className="w-16 shrink-0 font-heading font-bold text-accent-600">
                    {t.year}
                  </span>
                  <span className="relative pl-6 text-sm md:text-base text-foreground-700 leading-relaxed before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-primary-400">
                    {t.text}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* contact + copyright */}
          <section className="rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-foreground-700 mb-6">
              <p className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                邮箱：hello@aidesefang.example.com
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-weibo-line text-primary-500"></i>
                微博：@爱的色放电影
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-wechat-line text-primary-500"></i>
                微信公众号：爱的色放电影
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500"></i>
                内容更新：每周更新影评与片单
              </p>
            </div>
            <p className="text-xs text-foreground-500 leading-relaxed mb-6">
              本站为影视资讯与影评分享平台，所有影评、资料均来自编辑团队原创或整理，仅供学习交流使用。相关电影海报、剧照及影片版权归原权利人所有。如有内容相关问题，欢迎通过上方邮箱与我们联系。
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-chat-smile-2-line"></i>
                前往留言反馈
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-secondary-300 text-secondary-800 text-sm font-medium hover:bg-secondary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览电影分类
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}